// client/src/dnd/emitter_tool/engine/engine_parts/targeting/TokenToTarget.js
export default function installTokenToTarget(env) {
  const WIN = typeof window !== "undefined" ? window : null;
  const GLOBAL_KEY = "__ET_TokenToTargetSingleton";
  try {
    const prev = WIN && WIN[GLOBAL_KEY];
    if (prev && typeof prev.destroy === "function") prev.destroy();
  } catch {}

  const canvas = env?.canvas || null;
  const ctx = env?.ctx || null;
  const getOrigin = typeof env?.getOrigin === "function" ? env.getOrigin : () => ({ x: 0, y: 0 });
  const dispatch = typeof env?.dispatch === "function" ? env.dispatch : (n, d) => { try { WIN?.dispatchEvent(new CustomEvent(n, { detail: d })); } catch {} };

  if (!canvas || !ctx) {
    const api = { destroy(){}, draw(){}, pickStart(){}, onPointerMove(){}, onPointerEnter(){}, onPointerLeave(){}, onClick(){} };
    if (WIN) WIN[GLOBAL_KEY] = api;
    return api;
  }

  const host = canvas.parentElement;
  const prevPointer = canvas.style.pointerEvents;
  canvas.style.pointerEvents = "none";

  const DPR = () => Math.max(1, (WIN?.devicePixelRatio) || 1);
  const softClear = () => ctx.clearRect(0, 0, canvas.width, canvas.height);

  function draw(originCss, targetCss) {
    const r = DPR();
    const ox = originCss.x * r, oy = originCss.y * r;
    const tx = targetCss.xCss * r, ty = targetCss.yCss * r;
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#60a5fa";
    ctx.moveTo(ox, oy);
    ctx.lineTo(tx, ty);
    ctx.stroke();
    ctx.beginPath();
    ctx.fillStyle = "#93c5fd";
    ctx.arc(ox, oy, 4 * r, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = "#fca5a5";
    ctx.arc(tx, ty, 4 * r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  const LS_MOUSE_X = "EmitterTool:UI:lastMouseXCss";
  const LS_MOUSE_Y = "EmitterTool:UI:lastMouseYCss";
  const UI_FIRE_MODE = "EmitterTool:UI:fireMode";
  const UI_PICKED_X = "EmitterTool:UI:pickedOriginX";
  const UI_PICKED_Y = "EmitterTool:UI:pickedOriginY";
  const UI_PICKED_A = "EmitterTool:UI:pickedAngleDeg";
  const UI_LAST_A   = "EmitterTool:UI:lastAngleDeg";

  let lastMouseCss = null;
  let lastClient = null;
  let alive = true;
  let rafId = 0;

  // NEW: persist whether pointer is inside host; don’t drop the overlay when mouse stops
  let lastInside = false;
  let hoverActive = false;

  function isInSidebarTarget(t) {
    try { return !!(t && typeof t.closest === "function" && t.closest('[data-et-ui="sidebar"]')); } catch { return false; }
  }
  function isInsideHost(e) {
    if (!host) return false;
    const r = host.getBoundingClientRect();
    const x = e?.clientX ?? 0, y = e?.clientY ?? 0;
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }
  function toHostCssPoint(e) {
    const r = host.getBoundingClientRect();
    return { xCss: Math.max(0, Math.min(r.width, e.clientX - r.left)), yCss: Math.max(0, Math.min(r.height, e.clientY - r.top)), clientX: e.clientX, clientY: e.clientY };
  }

  const sendOverlayAt = (xCss, yCss, clientX, clientY) => {
    try {
      WIN?.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", {
        detail: {
          show: true,
          settings: { centerOn: "target", lockCenterOn: true },
          origin:   { x: xCss, y: yCss, xCss, yCss, clientX, clientY }
        }
      }));
    } catch {}
  };

  (function restore() {
    const rect = host.getBoundingClientRect();
    const xRaw = Number(localStorage.getItem(LS_MOUSE_X));
    const yRaw = Number(localStorage.getItem(LS_MOUSE_Y));
    if (Number.isFinite(xRaw) && Number.isFinite(yRaw)) {
      const xCss = Math.max(0, Math.min(rect.width, xRaw));
      const yCss = Math.max(0, Math.min(rect.height, yRaw));
      lastMouseCss = { xCss, yCss };
      // Don’t force clientX/Y on restore
      sendOverlayAt(xCss, yCss, null, null);
    }
  })();

  function renderOnce() {
    const r = DPR();
    const pr = host.getBoundingClientRect();
    const w = Math.max(1, Math.floor(pr.width * r));
    const h = Math.max(1, Math.floor(pr.height * r));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = pr.width + "px";
      canvas.style.height = pr.height + "px";
    }
    softClear();

    // Always keep the overlay at the last mouse position while inside, even when the mouse stops moving
    if (lastInside && lastMouseCss) {
      const tokenCss = getOrigin() || { x: 0, y: 0 };
      if (hoverActive) draw(tokenCss, lastMouseCss);
      sendOverlayAt(lastMouseCss.xCss, lastMouseCss.yCss, lastClient?.clientX ?? null, lastClient?.clientY ?? null);
    }
  }

  function loop() {
    if (!alive) return;
    rafId = requestAnimationFrame(loop);
    renderOnce();
  }

  const computeAngleDeg = (fromCss, toCss) => {
    const dx = toCss.xCss - fromCss.x;
    const dy = toCss.yCss - fromCss.y;
    return (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
  };

  const updateMouse = (e) => {
    const inside = isInsideHost(e);
    const inSidebar = isInSidebarTarget(e?.target);
    lastInside = inside && !inSidebar;
    hoverActive = lastInside;
    if (!lastInside) return;

    const p = toHostCssPoint(e);
    lastMouseCss = { xCss: p.xCss, yCss: p.yCss };
    lastClient = { clientX: p.clientX, clientY: p.clientY };
    try { localStorage.setItem(LS_MOUSE_X, String(p.xCss)); } catch {}
    try { localStorage.setItem(LS_MOUSE_Y, String(p.yCss)); } catch {}
    sendOverlayAt(p.xCss, p.yCss, p.clientX, p.clientY);
  };

  const onMouseEnter = () => { lastInside = true; };
  const onMouseLeave = () => {
    lastInside = false;
    hoverActive = false;
    lastMouseCss = null;
    // When we leave the host, allow other systems to fall back to token if they want
    try { WIN?.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", { detail: { settings: { lockCenterOn: false } } })); } catch {}
  };

  const onClick = (e) => {
    if (!isInsideHost(e)) return;
    if (isInSidebarTarget(e?.target)) return;
    const clickCss = toHostCssPoint(e);
    const tokenCss = getOrigin() || { x: 0, y: 0 };
    const angleDeg = computeAngleDeg(tokenCss, clickCss);
    sendOverlayAt(clickCss.xCss, clickCss.yCss, clickCss.clientX, clickCss.clientY);
    try {
      localStorage.setItem(UI_PICKED_X, String(tokenCss.x));
      localStorage.setItem(UI_PICKED_Y, String(tokenCss.y));
      localStorage.setItem(UI_PICKED_A, String(angleDeg));
      localStorage.setItem(UI_LAST_A, String(angleDeg));
      localStorage.setItem(UI_FIRE_MODE, "rule");
    } catch {}
    try { WIN?.dispatchEvent(new CustomEvent("EmitterTool:RulePhaseStart")); } catch {}
    const payload = { x: tokenCss.x, y: tokenCss.y, angleDeg, angle: angleDeg, rotationDeg: angleDeg, addRotDeg: angleDeg, shapeSettings: { rotationDeg: angleDeg }, effectSettings: { rotationDeg: angleDeg } };
    dispatch("EmitterTool:UI:PickedLocation", payload);
    dispatch("EmitterTool:UI:FireRule", payload);
  };

  const bind = () => {
    WIN?.addEventListener?.("pointermove", updateMouse, { passive: true, capture: true });
    WIN?.addEventListener?.("resize", renderOnce);
    WIN?.addEventListener?.("click", onClick, { capture: true });
    try {
      host.addEventListener("mouseenter", onMouseEnter, { passive: true });
      host.addEventListener("mouseleave", onMouseLeave, { passive: true });
    } catch {}
  };

  const unbind = () => {
    WIN?.removeEventListener?.("pointermove", updateMouse, { capture: true });
    WIN?.removeEventListener?.("resize", renderOnce);
    WIN?.removeEventListener?.("click", onClick, { capture: true });
    try {
      host.removeEventListener("mouseenter", onMouseEnter);
      host.removeEventListener("mouseleave", onMouseLeave);
    } catch {}
  };

  bind();
  loop();
  renderOnce();

  const api = {
    destroy() {
      alive = false;
      if (rafId) cancelAnimationFrame(rafId);
      unbind();
      softClear();
      canvas.style.pointerEvents = prevPointer ?? "";
      if (WIN && WIN[GLOBAL_KEY] === api) WIN[GLOBAL_KEY] = null;
      try { WIN?.dispatchEvent(new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", { detail: { settings: { lockCenterOn: false } } })); } catch {}
    },
    draw() {}, pickStart() {}, onPointerMove() {}, onPointerEnter() {}, onPointerLeave() {}, onClick() {},
  };

  if (WIN) WIN[GLOBAL_KEY] = api;
  return api;
}
